import React, { useEffect, useRef } from "react";
import FilterLabel from "./FilterLabel";
import { BiTime, BiUser } from "react-icons/bi";
import { useDispatch } from "react-redux";
import { getBackgroundColor, getMinutes } from "../../../utilities";
import { addChosenSticker } from "../../../redux/slices/stickers.slice";
import { modalEdit } from "../../../redux/slices/modal.slice";
import { defaultFilters } from "../../FiltersSidebar";
import { Draggable } from "@fullcalendar/interaction";
import moment from "moment/moment";
import { getDuration, getTime } from "../helpers";

// Extracts a solid colour from a potentially gradient background.
// Gradients are invalid as border-left colour values — this returns the first
// stop colour so the left-border accent always renders correctly.
const vividColor = (color) => {
  if (!color) return null;
  if (color.includes('gradient')) {
    // Pull the first colour stop out of the gradient string
    const match = color.match(/,\s*(#[\da-fA-F]{3,8}|hsl[a]?\([^)]+\)|[a-z]+)/);
    return match ? match[1] : null;
  }
  const hslMatch = color.match(/hsla?\(\s*([^,]+),\s*([^,]+),\s*([^,]+)(?:,\s*[^)]*)?\)/);
  if (hslMatch) return `hsl(${hslMatch[1]}, ${hslMatch[2]}, ${hslMatch[3]})`;
  if (color.startsWith('#')) return color.slice(0, 7);
  return color;
};

// Convert a solid CSS colour to rgba at the given alpha.
// Handles #rrggbb, hsl(...), and falls back to a neutral tint.
const toRgba = (color, alpha) => {
  if (!color) return `rgba(200,200,200,${alpha})`;
  if (color.startsWith('#')) {
    const hex = color.length === 4
      ? color.slice(1).split('').map(c => c + c).join('')
      : color.slice(1, 7);
    const r = parseInt(hex.slice(0, 2), 16);
    const g = parseInt(hex.slice(2, 4), 16);
    const b = parseInt(hex.slice(4, 6), 16);
    return `rgba(${r},${g},${b},${alpha})`;
  }
  if (color.startsWith('hsla(')) return color.replace(/,[^,)]+\)$/, `,${alpha})`);
  if (color.startsWith('hsl(')) return `hsla(${color.slice(4, -1)},${alpha})`;
  return color;
};

const EventsPull = ({
  eventsPull,
  onRemoveHandle,
  filters,
  colorBy = "levels",
}) => {
  const dispatch = useDispatch();
  const draggableRef = useRef(null);

  const openModal = (item) => {
    dispatch(addChosenSticker(item));
    dispatch(modalEdit("Stickers"));
  };

  useEffect(() => {
    if (draggableRef.current) {
      draggableRef.current.destroy();
    }

    const draggableEl = document.getElementById("events-pull");
    draggableRef.current = new Draggable(draggableEl, {
      itemSelector: ".fc-event",
      eventData: function (eventEl) {
        const data = JSON.parse(eventEl.getAttribute("data-event"));
        const currentColorBy = eventEl.getAttribute("data-color-by") || colorBy;
        const background = getBackgroundColor(data, currentColorBy);

        return {
          id: data.id,
          studioId: data.studioId,
          title: data.className,
          className: data.className,
          start: moment(data.date).format(),
          end: moment(data.date).add(data.length, "hours").format(),
          backgroundColor: background,
          background: background,
          teachers: data.teachers,
          teachersId: data.teachersId,
          styles: data.styles,
          levels: data.levels,
          duration: getDuration(data.length),
          extendedProps: {
            length: data.length,
            styles: data.styles,
            levels: data.levels,
            teachers: data.teachers,
            teachersId: data.teachersId,
            levelsId: data.levelsId,
          },
        };
      },
    });

    return () => {
      if (draggableRef.current) {
        draggableRef.current.destroy();
      }
    };
  }, [colorBy, eventsPull]);

  return (
    <div id="events-pull" className="searchResults mt-3">
      {Object.keys(defaultFilters).map((filterName) => (
        <FilterLabel
          filters={filters}
          filterName={filterName}
          onRemoveHandle={onRemoveHandle}
          key={filterName}
        />
      ))}
      <h5>Search results</h5>
      <div id="external-events" className="cardsPull">
        {eventsPull.map((item, index) => {
          const background = getBackgroundColor(item, colorBy);
          const solidColor = vividColor(background);
          // rgba tint for background — same 38% opacity as schedule cards, no overlay div needed
          const tintedBg = toRgba(solidColor, 0.38);
          // Use solid accent for border — gradients are invalid as border-color values
          const borderColor = solidColor || '#D9D9D9';
          const teacherLabel = item.teachers?.filter(Boolean).join(", ") || "";

          return (
            <div
              onClick={() => openModal(item)}
              key={`eventsPull-${index}`}
              className="fc-event events"
              data-event={JSON.stringify(item)}
              data-color-by={colorBy}
              style={{
                background: tintedBg,
                backgroundColor: tintedBg,
                borderLeft: `3px solid ${borderColor}`,
                position: 'relative',
                overflow: 'hidden',
                marginBottom: '6px',
                borderRadius: '4px',
                padding: '6px 8px',
                cursor: 'pointer',
                minHeight: '68px',
              }}
            >


              {/* Class name */}
              <div style={{
                fontWeight: 700,
                color: '#1a1a1a',
                position: 'relative',
                zIndex: 1,
                overflow: 'hidden',
                whiteSpace: 'nowrap',
                textOverflow: 'ellipsis',
                fontSize: '13px',
                lineHeight: '1.3',
              }}>
                {item.className || "none"}
              </div>
              {/* Teacher row — always rendered, blank if no teacher */}
              <div style={{
                display: 'flex',
                alignItems: 'center',
                color: '#444',
                position: 'relative',
                zIndex: 1,
                fontSize: '12px',
                overflow: 'hidden',
                lineHeight: '1.3',
                minHeight: '18px',
              }}>
                <BiUser size={14} color={teacherLabel ? "#888" : "transparent"} style={{ flexShrink: 0, marginRight: '4px' }} />
                <span style={{ overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}>
                  {teacherLabel}
                </span>
              </div>
              {/* Duration */}
              <div style={{
                display: 'flex',
                alignItems: 'center',
                color: '#555',
                position: 'relative',
                zIndex: 1,
                fontSize: '12px',
                lineHeight: '1.3',
              }}>
                <BiTime size={14} color="#888" style={{ flexShrink: 0, marginRight: '4px' }} />
                <span>{item.length ? getTime(getMinutes(item.length)) : ""}</span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default EventsPull;
